import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { TenantTypeEnum } from '@zaoblako/shared/modules/saas/enums/tenant.type.enum';
import { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { UserAvatarComponent } from '@zaoblako/web/shared/components/user-avatar/user-avatar.component';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PanelMenuModule } from 'primeng/panelmenu';

@Component({
  standalone: true,
  selector: 'crm-menu-component',
  templateUrl: 'crm-menu.component.html',
  imports: [CommonModule, UserAvatarComponent, PanelMenuModule, RouterModule, DialogModule, ButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CrmMenuComponent implements OnInit, OnChanges {
  @Input() public minimized: boolean;

  public items: MenuItem[];

  public currentUser: UserFindInterface;

  public isShowTenantDialog = false;
  public tenants: { name: string; subdomain: string }[] = [];

  public constructor(
    private readonly authService: AuthService,
    public readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    this.currentUser = this.authService.currentUser;

    this.globalService
      .select((state) => state.tenantInfo)
      .subscribe((tenantInfo) => {
        this.items = [
          {
            label: 'Рабочий стол',
            icon: 'pi pi-desktop',
            routerLink: '/',
            routerLinkActiveOptions: { exact: true },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('BASIS')
          },
          {
            label: 'Задачи',
            icon: 'pi pi-check-square',
            routerLink: '/project',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('PROJECT')
          },
          {
            label: 'Платежи',
            icon: 'pi pi-dollar',
            routerLink: '/payments',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('FINANCE')
          },
          {
            label: 'Закупки',
            icon: 'pi pi-table',
            routerLink: '/purchase',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('PROCUREMENT')
          },
          {
            label: 'Бюджетирование',
            icon: 'pi pi-chart-bar',
            routerLink: '/budget',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('FINANCE')
          },
          {
            label: 'ОФД',
            icon: 'pi pi-book',
            routerLink: '/ofd',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('FDO')
          },
          {
            label: 'Документы',
            icon: 'pi pi-file',
            routerLink: '/document',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('FINANCE')
          },
          {
            label: 'Задолженности',
            icon: 'pi pi-info-circle',
            routerLink: '/debts',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('DEBTS')
          },
          {
            label: 'Проекты',
            icon: 'pi pi-briefcase',
            routerLink: '/deal',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('FINANCE')
          },
          {
            label: 'Компании',
            icon: 'pi pi-building',
            routerLink: '/company',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('BASIS')
          },
          {
            label: 'Клиенты',
            icon: 'pi pi-id-card',
            routerLink: '/client',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('BASIS')
          },
          {
            label: 'Пользователи',
            icon: 'pi pi-users',
            routerLink: '/user',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('BASIS')
          },
          {
            label: 'Справочники',
            icon: 'pi pi-list',
            routerLink: '/directory',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('BASIS')
          },
          {
            label: 'HelpDesk',
            icon: 'pi pi-question-circle',
            routerLink: '/helpdesk',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('HELPDESK')
          },
          {
            label: 'Пропуска',
            icon: 'pi pi-key',
            routerLink: '/control',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('PACS')
          },
          {
            label: 'Проезд',
            icon: 'pi pi-car',
            routerLink: '/passage',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('CCTV')
          },
          {
            label: 'Трафик',
            icon: 'pi pi-sort-alt',
            routerLink: '/traffic',
            routerLinkActiveOptions: { exact: false },
            visible: (tenantInfo?.ratePlan?.availableModules || []).includes('Traffic')
          },
          {
            label: 'Админ панель',
            icon: 'pi pi-cog',
            routerLink: '/admin',
            routerLinkActiveOptions: { exact: false },
            visible:
              tenantInfo?.type === TenantTypeEnum.ADMIN &&
              (this.globalService.globalInfo?.config?.multiTenancyEnabled ||
                this.authService.currentProfile?.isSuperAdmin)
          }
        ];
        this.updateTooltipMenuItem();
        this.cdRef.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.minimized) {
      this.updateTooltipMenuItem();
    }
  }

  private updateTooltipMenuItem() {
    if (this.minimized && this.items?.length) {
      this.items = this.items.map((item) => ({
        ...item,
        tooltipOptions: {
          tooltipLabel: item.label,
          tooltipPosition: 'right',
          tooltipStyleClass: 'menu-item-tooltip',
          showDelay: 200,
          life: 3000
        }
      }));
      this.cdRef.markForCheck();
    }
    if (!this.minimized && this.items?.length) {
      this.items = this.items.map((item) => ({ ...item, tooltipOptions: null }));
      this.cdRef.markForCheck();
    }
  }

  public logout() {
    this.authService.logout(location.pathname + location.search);
  }

  public openTenantDialog() {
    this.isShowTenantDialog = true;
    this.cdRef.markForCheck();

    this.tenants = [];

    Object.keys(this.authService.currentProfile.usersPerSubdomain).forEach((key) => {
      if (
        this.authService.currentProfile.usersPerSubdomain[key].tenant?.id !==
        this.globalService.globalInfo.tenantInfo.id
      ) {
        this.tenants.push({
          name: this.authService.currentProfile.usersPerSubdomain[key].tenant?.name,
          subdomain: key
        });
      }
    });

    Object.keys(this.authService.currentProfile.externalUsersPerSubdomain).forEach((key) => {
      if (
        this.authService.currentProfile.externalUsersPerSubdomain[key].tenant?.id !==
        this.globalService.globalInfo.tenantInfo.id
      ) {
        this.tenants.push({
          name: this.authService.currentProfile.externalUsersPerSubdomain[key].tenant?.name,
          subdomain: key
        });
      }
    });
  }

  public redirectToTenant(tenant: { name: string; subdomain: string }) {
    window.open(`https://${tenant.subdomain}/authentication?token=${localStorage.getItem('JWT_TOKEN')}`, '_blank');
  }
}
